import React, { useState } from "react"
import PropTypes from "prop-types"
import { Alert, Button, Flex, Form, Row, Col } from "antd"
import { CustomInput } from "ui/Input"
import { CustomForm } from "ui/Form"
import { Text } from "ui/Typography"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import useFormErrors from "hooks/useAntdFormErrors"
import { CountrySelector } from "ui/Input/CountrySelector"
import useValidateAddress from "modules/google/hooks/useValidateAddress"
import useGeocoder from "modules/google/hooks/useGeocoder"
import Spinner from "components/common/Spinner"
import { Confirmed } from "ui/Icon/DSIcon/Confirmed"

export const CustomAddress = ({ backToSearch, form, initialValues, setCenterLatLng, setMeetingLatLng }) => {
  const { t } = useTranslation()
  const requiredErrorMessage = t("form_validation.cannot_be_blank")
  const confirmAddressLabel = t("listings.tabs.location.confirm_address")
  const confirmedLabel = t("listings.tabs.location.confirmed")
  const geocoder = useGeocoder()
  const [isButtonDisabled, setIsButtonDisabled] = useState(true)
  const [buttonLabel, setButtonLabel] = useState(confirmAddressLabel)
  useFormErrors(form)

  const selectedCountry = Form.useWatch("country", form)
  const isBahamas = selectedCountry === "BS"
  const isZipRequired = !isBahamas
  const isStreetAddressRequired = !isBahamas
  const isCityRequired = !isBahamas
  const isStateRequired = !isBahamas

  const { handleConfirmAddress, loading, errorMessage, suggestedAddress, suggestedComponents, clearError } = useValidateAddress(
    geocoder,
    form,
    setCenterLatLng,
    setMeetingLatLng,
    setIsButtonDisabled,
    setButtonLabel
  )

  const handleValuesChange = () => {
    setIsButtonDisabled(false)
    setButtonLabel(confirmAddressLabel)
  }

  const handleUseSuggestedAddress = () => {
    if (suggestedComponents) {
      form.setFieldsValue(suggestedComponents)
      setIsButtonDisabled(false)
      clearError()
    }
  }

  const ADDRESS_FIELDS = isBahamas
    ? ["country"]
    : ["street_address", "city", "state", "zip_code", "country"]

  const handleConfirmClick = async () => {
    try {
      await form.validateFields(ADDRESS_FIELDS)
      setButtonLabel(t("listings.tabs.location.confirming"))
      const response = await handleConfirmAddress()
      if (response) {
        setIsButtonDisabled(true)
        setButtonLabel(confirmedLabel)
      } else {
        setButtonLabel(confirmAddressLabel)
      }
    } catch {
      setButtonLabel(confirmAddressLabel)
    }
  }

  const renderButtonLabel = () => {
    if (buttonLabel === confirmedLabel) {
      return (
        <Flex align="center" justify="center">
          <Confirmed className="mr-s" />
          {buttonLabel}
        </Flex>
      )
    }
    return buttonLabel
  }

  return (
    <>
      <Text className="align-center mb-l" variant="link" onClick={backToSearch}>
        <Icon type="feather" name="ArrowLeft" className="mr-xs" width={16} height={16} /> {t("pro.addresses.back_to_search")}
      </Text>
      <CustomForm form={form} layout="vertical" initialValues={initialValues} onValuesChange={handleValuesChange}>
        <Form.Item
          label={t("pro.addresses.street_address")}
          name="street_address"
          required={isStreetAddressRequired}
          rules={isStreetAddressRequired ? [{ required: true, message: requiredErrorMessage }] : []}
        >
          <CustomInput />
        </Form.Item>

        <Row gutter={16}>
          <Col md={8} sm={24} xs={24}>
            <Form.Item
              label={t("pro.addresses.city")}
              name="city"
              required={isCityRequired}
              rules={isCityRequired ? [{ required: true, message: requiredErrorMessage }] : []}
            >
              <CustomInput />
            </Form.Item>
          </Col>

          <Col md={8} sm={24} xs={24}>
            <Form.Item
              label={t("pro.addresses.state")}
              name="state"
              required={isStateRequired}
              rules={isStateRequired ? [{ required: true, message: requiredErrorMessage }] : []}
            >
              <CustomInput />
            </Form.Item>
          </Col>

          <Col md={8} sm={24} xs={24}>
            <Form.Item
              label={t("pro.addresses.zip")}
              name="zip_code"
              required={isZipRequired}
              rules={isZipRequired ? [{ required: true, message: requiredErrorMessage }] : []}
            >
              <CustomInput />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          label={t("pro.addresses.country")}
          name="country"
          required={true}
          rules={[{ required: true, message: requiredErrorMessage }]}
        >
          <CountrySelector />
        </Form.Item>
        <Form.Item name="latitude" hidden>
          <CustomInput />
        </Form.Item>
        <Form.Item name="longitude" hidden>
          <CustomInput />
        </Form.Item>

        {errorMessage && (
          <Alert
            message={errorMessage}
            type="error"
            showIcon
            className="mb-m"
            action={
              suggestedAddress && (
                <Button size="small" type="primary" onClick={handleUseSuggestedAddress}>
                  {t("listings.tabs.location.use_suggested")}
                </Button>
              )
            }
            description={
              suggestedAddress && (
                <div className="mt-s">
                  <strong>{t("listings.tabs.location.suggested_address")}:</strong>
                  <div>{suggestedAddress}</div>
                </div>
              )
            }
          />
        )}
        <Button type="primary" size="large" onClick={handleConfirmClick} disabled={isButtonDisabled} loading={loading}>
          {renderButtonLabel()}
        </Button>
      </CustomForm>
    </>
  )
}

CustomAddress.propTypes = {
  backToSearch: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  initialValues: PropTypes.object,
  setCenterLatLng: PropTypes.func,
  setMeetingLatLng: PropTypes.func
}
